import links from 'helpers/links';
import Nprogress from 'nprogress';
import { push } from 'redux-first-history';
import { all, call, cancelled, put, select, spawn, takeEvery } from 'redux-saga/effects';
import projectActions from 'redux/project/actions';
import tourActions from 'redux/tour/actions';
import actions from './actions';
import createApplicationGraphql from './graphql';
import requestGenerator from './requestGenerator';
import { uploadDocumentRequest } from './requests';

function* uploadDocumentGenerator(action) {
  try {
    yield call(() => {
      Nprogress.start();
    });

    const requests = action.payload.map((file) => call(uploadDocumentRequest, file));
    const responses = yield all(requests);

    yield put({
      type: actions.UPLOAD_DOCUMENT_DONE,
      payload: responses,
    });
  } catch (e) {
    log(e);
    yield put({ type: actions.UPLOAD_DOCUMENT_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: actions.UPLOAD_DOCUMENT_CANCELLED,
      });
    }
  }
}

export function* createApplicationGenerator(action) {
  try {
    yield call(() => {
      Nprogress.start();
    });
    const data = action.payload;

    const requestData = requestGenerator(data);
    const response = yield call(createApplicationGraphql, {
      application: requestData,
    });

    yield put({
      type: actions.CREATE_APPLICATION_DONE,
    });

    // An uploaded candidate belongs to the project, not to a job: the gateway filters a job-scoped
    // list on the application's job id, and the job's application_count is a passthrough that never
    // reflects it. So while a job filter is active (always the case on the post-order thank-you
    // page) a plain refetch comes back empty and the thank-you page stays. Clear the job filter the
    // same way the PageSelector's "all applications" option does; the SET_FILTER watcher performs
    // the refetch. The whole-project view, unlike the "added candidates" one, also keeps showing
    // applications that arrive once the ad is published.
    const filterByJobId = yield select((state) =>
      state.Project.getIn(['selectedFilters', 'filterByJobId'])
    );

    if (filterByJobId) {
      yield put(
        projectActions.setFilter({
          group: 'filterByJobId',
          filters: null,
          variables: {
            id: requestData.projectId,
          },
          recache: true,
        })
      );
    } else {
      yield put({
        type: projectActions.FETCH_PROJECT_START,
        payload: {
          variables: {
            id: requestData.projectId,
          },
          recache: true,
        },
      });
    }

    if (response.data.mutateApplication.id) {
      yield put(
        push(links.application(requestData.projectId, response.data.mutateApplication.id).url)
      );
    }
    yield put({
      type: tourActions.VERIFY_CHECKPOINT_START,
      payload: {
        section: 'tags',
      },
    });
  } catch (e) {
    log(e);
    yield put({ type: actions.CREATE_APPLICATION_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: actions.CREATE_APPLICATION_CANCELLED,
      });
    }
  }
}

function* uploadDocument() {
  yield takeEvery(actions.UPLOAD_DOCUMENT_START, uploadDocumentGenerator);
}

function* createApplication() {
  yield takeEvery(actions.CREATE_APPLICATION_START, createApplicationGenerator);
}

export default function* rootSaga() {
  yield all([spawn(uploadDocument), spawn(createApplication)]);
}
